'use client'

import Link from 'next/link'
import { useEffect, useState, type ReactNode } from 'react'
import { CookieSettingsLink } from '@/components/ConsentBanner'
import { Sidebar } from '@/components/Sidebar'
import { MarketingHeader } from '@/components/MarketingHeader'
import { useAuth } from '@/components/AuthProvider'
import Reveal from '@/components/Reveal'
import { track } from '@/lib/analytics'
import { DesktopLinkForm } from './DesktopLinkForm'

// Building blocks for the extension's ad landing pages (/uk-job-applications,
// /nhs-job-applications, /civil-service-job-applications). Ads must land on
// these rather than on the Chrome Web Store: the visit sets the ad-click
// cookie on cvcircuit.com, so the install conversion (fired by
// /welcome?from=install) can be tied back to the ad.

export const STORE_URL = 'https://chromewebstore.google.com/detail/cvcircuit/oajodloniilndmkhndlmdhdcbdolmoln'
// The Edge Add-ons listing, once published (docs/marketing/edge-addons.md).
// While empty, Edge visitors get the Chrome Web Store, which Edge can also
// install from.
export const EDGE_STORE_URL = ''

export const PRICE_ANSWER = 'Installing is free and includes 5 free Pro actions, so you can try it on real applications. Pro is £23.95 a month or £60.95 every three months, and you can cancel any time.'

export function StoreButton({ placement, light = false }: { placement: string; light?: boolean }) {
  // Decided after load, so the pre-rendered page (Chrome) matches the first render.
  const [edge, setEdge] = useState(false)
  useEffect(() => { setEdge(!!EDGE_STORE_URL && navigator.userAgent.includes('Edg/')) }, [])
  return (
    <a
      href={edge ? EDGE_STORE_URL : STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track('store_click', { placement, store: edge ? 'edge' : 'chrome' })}
      className={`inline-flex items-center justify-center px-8 py-3.5 rounded-lg text-base font-semibold transition-colors ${
        light ? 'bg-white text-primary-600 hover:bg-primary-50' : 'bg-primary-600 hover:bg-primary-700 text-white'
      }`}
    >
      {edge ? 'Add to Edge' : 'Add to Chrome'} — it&apos;s free
    </a>
  )
}

export function LandingShell({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  return (
    <div className="cv-home min-h-screen bg-slate-100">
      <MarketingHeader onMenuClick={() => setIsSidebarOpen((v) => !v)} />
      <div className="flex lg:mt-8">
        {user && <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />}
        <div className="flex-1 min-w-0 bg-white">
          <main>{children}</main>
          <footer className="border-t py-8 mt-4">
            <div className="container mx-auto px-4 text-center text-gray-600">
              <p>&copy; 2026 CVCircuit. All rights reserved.</p>
              <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
                <Link href="/features" className="text-primary-600 font-semibold hover:underline">Features</Link>
                <Link href="/pricing" className="text-primary-600 font-semibold hover:underline">Pricing</Link>
                <Link href="/terms" className="text-primary-600 font-semibold hover:underline">Terms</Link>
                <Link href="/privacy" className="text-primary-600 font-semibold hover:underline">Privacy</Link>
                <CookieSettingsLink />
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}

export type FormExampleProps = {
  step: string
  label: string
  table?: { columns: string[]; rows: string[][] }
  questions: { q: string; a: string }[]
}

// A drawn example of a form section after a fill, not a screenshot of any
// real employer's system.
export function FormExample({ step, label, table, questions }: FormExampleProps) {
  return (
    <div role="img" aria-label={label} className="rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden text-left">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{step}</span>
        <span className="text-xs text-gray-400">Example</span>
      </div>
      <div className="px-5 py-4">
        {table && (
          <div
            className="grid gap-x-3 gap-y-2 text-xs sm:text-sm"
            style={{ gridTemplateColumns: `1fr 1.6fr ${table.columns.slice(2).map(() => 'auto').join(' ')}` }}
          >
            {table.columns.map((h) => (
              <span key={h} className="font-semibold text-gray-500">{h}</span>
            ))}
            {table.rows.map((r) => r.map((cell, i) => (
              <span key={`${r[1]}-${i}`} className="rounded-md border border-green-200 bg-green-50 px-2 py-1 text-gray-800">{cell}</span>
            )))}
          </div>
        )}
        <div className={`${table ? 'mt-5' : ''} space-y-3 text-xs sm:text-sm`}>
          {questions.map((item) => (
            <div key={item.q}>
              <p className="text-gray-600">{item.q}</p>
              <p className="mt-1 inline-block rounded-md border border-green-200 bg-green-50 px-2 py-1 text-gray-800">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-gray-100 bg-primary-50 px-5 py-3 text-xs sm:text-sm text-primary-700 font-medium">
        ✓ Filled from your Career Bank. Check it, then continue.
      </div>
    </div>
  )
}

export function Hero({ badge, title, highlight, intro, example }: {
  badge: string
  title: string
  highlight: string
  intro: string
  example: FormExampleProps
}) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary-50 via-white to-white" />
      <div className="pointer-events-none absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-br from-primary-200/40 via-primary-100/20 to-transparent blur-3xl" />
      <div className="relative container mx-auto px-4 py-12 sm:py-20">
        <div className="max-w-6xl mx-auto grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center rounded-full border border-primary-200/60 bg-white/80 px-4 py-2 text-sm font-semibold text-primary-700 shadow-sm mb-6">
              {badge}
            </div>
            <Reveal>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
                {title}
                <span className="text-primary-600"> {highlight}</span>
              </h1>
            </Reveal>
            <Reveal delayMs={120}>
              <p className="mt-5 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0">{intro}</p>
            </Reveal>
            <Reveal delayMs={220}>
              <div className="mt-8 hidden md:flex md:justify-center lg:justify-start">
                <StoreButton placement="hero" />
              </div>
              <p className="mt-3 hidden md:block text-sm text-gray-500">
                Free to install · Works in Google Chrome on a computer · Never submits for you
              </p>
              <DesktopLinkForm placement="hero" className="mt-8" />
            </Reveal>
          </div>
          <Reveal delayMs={160}>
            <FormExample {...example} />
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export function SystemsStrip({ heading, systems, note }: { heading: string; systems: string[]; note: string }) {
  return (
    <section className="bg-gray-50">
      <div className="container mx-auto px-4 py-10 sm:py-12">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-5">{heading}</p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {systems.map((name) => (
                <span key={name} className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 shadow-sm">
                  {name}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm text-gray-500 max-w-xl mx-auto">{note}</p>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export function FeatureGrid({ title, highlight, intro, items }: {
  title: string
  highlight: string
  intro: string
  items: { title: string; body: string }[]
}) {
  return (
    <section className="container mx-auto px-4 py-12 sm:py-16">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {title} <span className="text-primary-600">{highlight}</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-3xl">{intro}</p>
        </Reveal>
        <Reveal delayMs={140}>
          <div className="mt-8 grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <div key={item.title} className="rounded-xl border border-primary-100 bg-white p-6 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

const HOW_IT_WORKS = [
  { step: 'Step 1', title: 'Add CVCircuit to Chrome', body: 'It’s free, and creating your account takes a minute.' },
  { step: 'Step 2', title: 'Fill in your Career Bank once', body: 'Your jobs, education, references and the questions UK forms always ask.' },
  { step: 'Step 3', title: 'Open an application and fill it in', body: 'Open the CVCircuit panel on the form, fill the page, check it and move on to the next one.' },
]

export function HowItWorks({ steps = HOW_IT_WORKS }: { steps?: { step: string; title: string; body: string }[] }) {
  return (
    <section className="container mx-auto px-4 py-12 sm:py-16">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">How it works</h2>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.step} delayMs={i * 100}>
              <div className="h-full bg-white rounded-xl border border-primary-100 p-6 shadow-sm">
                <div className="text-sm font-semibold text-primary-600 mb-2">{s.step}</div>
                <h3 className="text-lg font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-gray-600 text-sm">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

const IN_CONTROL = [
  { title: 'Nothing is submitted for you', body: 'You check each page and press submit yourself. Unlike apply-to-hundreds bots, nothing goes out without you seeing it.' },
  { title: 'No made-up answers', body: "Answers come from your Career Bank. If it doesn't know something, the question is left for you rather than guessed." },
  { title: 'Declarations are yours', body: "Declarations are only answered if you've switched them on in your Career Bank. Otherwise they're left for you." },
]

export function InControl({ items = IN_CONTROL }: { items?: { title: string; body: string }[] }) {
  return (
    <section className="bg-gray-50">
      <div className="container mx-auto px-4 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              It does the typing. <span className="text-primary-600">You stay in charge.</span>
            </h2>
          </Reveal>
          <Reveal delayMs={120}>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {items.map((item) => (
                <div key={item.title} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export function FaqSection({ faqs }: { faqs: { q: string; a: string }[] }) {
  return (
    <section className="container mx-auto px-4 py-12 sm:py-16">
      <div className="max-w-3xl mx-auto">
        <Reveal><h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">Common questions</h2></Reveal>
        <div className="space-y-5">
          {faqs.map((faq) => (
            <div key={faq.q} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
          <p className="text-sm text-gray-500">
            More detail in our <Link href="/privacy" className="text-primary-600 font-semibold hover:underline">privacy policy</Link>.
          </p>
        </div>
      </div>
    </section>
  )
}

export function FinalCta({ title, body }: { title: string; body: string }) {
  return (
    <section className="container mx-auto px-4 pb-12 sm:pb-16">
      <div className="max-w-3xl mx-auto rounded-2xl bg-gradient-to-br from-primary-600 to-primary-800 p-8 sm:p-12 text-center text-white">
        <h2 className="text-2xl sm:text-3xl font-bold">{title}</h2>
        <p className="mt-3 text-primary-100 max-w-lg mx-auto">{body}</p>
        <div className="mt-6 hidden md:flex justify-center">
          <StoreButton placement="footer_cta" light />
        </div>
        <DesktopLinkForm placement="footer_cta" light className="mt-6" />
      </div>
    </section>
  )
}
